import { useCallback, useEffect, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { type SlackConfigResponse } from '@resolve-ai/shared';
import { projectsApi } from '../lib/projects-api';
import { integrationsApi } from '../lib/integrations-api';
import { tasksApi } from '../lib/tasks-api';
import { knowledgeApi } from '../lib/knowledge-api';
import type { ProjectWithConfigs } from '../lib/projects-api';
import type { TicketListResponse } from '@resolve-ai/shared';
import { ticketsApi } from '../lib/tickets-api';
import type { Repository, JiraUser, JiraStatus } from '../lib/integrations-api';
import { useInfiniteScroll } from './useInfiniteScroll';
import { useIntegrationStatuses } from './useIntegrationStatuses';
import { useAuth } from '../contexts/AuthContext';

interface EditBranchConfig {
  id: string;
  repoOwner: string;
  repoName: string;
  repoFullName: string;
  defaultBranch: string;
}

type DetailModalType = 'addRepo' | 'editBranch' | 'slack' | 'jira' | null;

export function useProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'tasks';

  const setActiveTab = useCallback(
    (tab: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (tab === 'tasks') {
          next.delete('tab');
        } else {
          next.set('tab', tab);
        }
        return next;
      });
    },
    [setSearchParams]
  );

  const [taskSearchQuery, setTaskSearchQuery] = useState('');
  const [debouncedTaskSearchQuery, setDebouncedTaskSearchQuery] = useState('');
  const { githubStatus: githubIntegrationStatus, jiraStatus: jiraIntegrationStatus } = useIntegrationStatuses();

  const [toast, setToast] = useState<{
    isOpen: boolean;
    message: string;
    variant: 'success' | 'error' | 'warning' | 'info';
  }>({
    isOpen: false,
    message: '',
    variant: 'info',
  });

  const [activeModal, setActiveModal] = useState<DetailModalType>(null);

  const [availableRepos, setAvailableRepos] = useState<Repository[]>([]);
  const [selectedRepoToAdd, setSelectedRepoToAdd] = useState<Repository | null>(null);
  const [isLoadingRepos, setIsLoadingRepos] = useState(false);
  const [repoToDelete, setRepoToDelete] = useState<{ id: string; name: string } | null>(null);

  const [editBranchConfig, setEditBranchConfig] = useState<EditBranchConfig | null>(null);
  const [branches, setBranches] = useState<{ name: string }[]>([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);

  const [slackWebhookUrl, setSlackWebhookUrl] = useState('');
  const [originalSlackWebhookUrl, setOriginalSlackWebhookUrl] = useState('');
  const [slackToDelete, setSlackToDelete] = useState(false);

  const [jiraAssignableUsers, setJiraAssignableUsers] = useState<JiraUser[]>([]);
  const [selectedAssigneeAccountId, setSelectedAssigneeAccountId] = useState('');
  const [originalAssigneeAccountId, setOriginalAssigneeAccountId] = useState('');
  const [isLoadingJiraUsers, setIsLoadingJiraUsers] = useState(false);
  const [jiraStatuses, setJiraStatuses] = useState<JiraStatus[]>([]);
  const [selectedStartStatus, setSelectedStartStatus] = useState('');
  const [selectedEndStatus, setSelectedEndStatus] = useState('');
  const [originalStartStatus, setOriginalStartStatus] = useState('');
  const [originalEndStatus, setOriginalEndStatus] = useState('');
  const [isLoadingJiraStatuses, setIsLoadingJiraStatuses] = useState(false);
  const [jiraConfigValidationError, setJiraConfigValidationError] = useState('');

  const showToast = (message: string, variant: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setToast({ isOpen: true, message, variant });
  };

  const closeToast = () => {
    setToast((current) => ({ ...current, isOpen: false }));
  };

  const detailQuery = useQuery<{ project: ProjectWithConfigs; slackConfig: SlackConfigResponse | null }>({
    queryKey: ['project-detail', id],
    enabled: !!id,
    queryFn: async () => {
      if (!id) {
        throw new Error('Project id is required');
      }

      const [project, slackConfig] = await Promise.all([projectsApi.get(id), projectsApi.getSlackConfig(id)]);

      return { project, slackConfig };
    },
  });

  const tasksQuery = useInfiniteQuery<TicketListResponse>({
    queryKey: ['project-tickets', id, debouncedTaskSearchQuery],
    enabled: !!id,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      if (!id) {
        throw new Error('Project id is required');
      }

      const cursor = typeof pageParam === 'string' ? pageParam : undefined;

      return ticketsApi.list(id, {
        cursor,
        search: debouncedTaskSearchQuery || undefined,
      });
    },
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? (lastPage.pagination.nextCursor ?? undefined) : undefined,
  });

  const knowledgeEntriesQuery = useQuery({
    queryKey: ['knowledge-entries', id],
    enabled: !!id,
    queryFn: () => knowledgeApi.list(id!),
  });

  const project = detailQuery.data?.project ?? null;
  const slackConfig = detailQuery.data?.slackConfig ?? null;
  const pinnedKnowledgeCount = (knowledgeEntriesQuery.data ?? []).filter((entry) => entry.pinned).length;
  const tickets = tasksQuery.data?.pages.flatMap((page) => page.tickets) ?? [];
  const ticketCount = tasksQuery.data?.pages[0]?.totalCount ?? 0;
  const hasTaskData = (tasksQuery.data?.pages.length ?? 0) > 0;
  const hasMoreTasks = !!tasksQuery.hasNextPage;
  const isLoadingMoreTasks = tasksQuery.isFetchingNextPage;

  const isSearching =
    taskSearchQuery !== debouncedTaskSearchQuery ||
    (tasksQuery.isFetching && !tasksQuery.isFetchingNextPage && !tasksQuery.isLoading);
  const isLoading = detailQuery.isLoading || tasksQuery.isLoading;
  const error = detailQuery.isError || (tasksQuery.isError && !hasTaskData) ? 'Failed to load project' : null;

  const refreshProjectDetail = async () => {
    if (!id) return;
    await queryClient.invalidateQueries({ queryKey: ['project-detail', id] });
  };

  const addRepoMutation = useMutation({
    mutationFn: async (repo: Repository) => {
      if (!id) {
        throw new Error('Project id is required');
      }

      await projectsApi.addGitHubConfig(id, {
        repoOwner: repo.owner.login,
        repoName: repo.name,
        defaultBranch: repo.default_branch,
      });
    },
  });

  const deleteRepoMutation = useMutation({
    mutationFn: async (configId: string) => {
      if (!id) {
        throw new Error('Project id is required');
      }
      await projectsApi.deleteGitHubConfig(id, configId);
    },
  });

  const saveSlackMutation = useMutation({
    mutationFn: async (webhookUrl: string) => {
      if (!id) {
        throw new Error('Project id is required');
      }
      await projectsApi.upsertSlackConfig(id, { webhookUrl: webhookUrl.trim() });
    },
  });

  const deleteSlackMutation = useMutation({
    mutationFn: async () => {
      if (!id) {
        throw new Error('Project id is required');
      }
      await projectsApi.deleteSlackConfig(id);
    },
  });

  const saveBranchMutation = useMutation({
    mutationFn: async ({ configId, defaultBranch }: { configId: string; defaultBranch: string }) => {
      if (!id) {
        throw new Error('Project id is required');
      }
      await projectsApi.updateGitHubConfig(id, configId, { defaultBranch });
    },
  });

  const saveJiraConfigMutation = useMutation({
    mutationFn: async (payload: {
      jiraProjectKey: string;
      jiraProjectName?: string;
      jiraFilterJql?: string;
      assignee?: string;
      assigneeDisplayName?: string;
      startStatus?: string;
      endStatus?: string;
    }) => {
      if (!id) {
        throw new Error('Project id is required');
      }
      await projectsApi.upsertJiraConfig(id, payload);
    },
  });

  const manualImportMutation = useMutation({
    mutationFn: async (ticketKey: string) => {
      if (!id) {
        throw new Error('Project id is required');
      }
      await projectsApi.manualImportTask(id, ticketKey);
    },
  });

  const cancelTaskMutation = useMutation({
    mutationFn: async (taskId: string) => {
      await tasksApi.cancelTask(taskId);
    },
  });

  const loadMoreTasks = useCallback(async () => {
    if (!hasMoreTasks || isLoadingMoreTasks) return;

    try {
      await tasksQuery.fetchNextPage();
    } catch (err) {
      console.error('Failed to load more tasks:', err);
      showToast('Failed to load more tasks', 'error');
    }
  }, [hasMoreTasks, isLoadingMoreTasks, tasksQuery]);

  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: loadMoreTasks,
    hasMore: hasMoreTasks,
    isLoading: isLoadingMoreTasks,
  });

  const handleSearchChange = useCallback((query: string) => {
    setTaskSearchQuery(query);
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedTaskSearchQuery(taskSearchQuery);
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [taskSearchQuery]);

  const handleOpenAddRepoModal = async () => {
    setIsLoadingRepos(true);
    try {
      const repos = await queryClient.fetchQuery({
        queryKey: ['integration-repositories'],
        queryFn: () => integrationsApi.getRepositories(),
        staleTime: 1000 * 60 * 5,
      });
      const linkedRepoNames = project?.githubConfigs.map((config) => config.repoFullName) || [];
      const available = repos.filter((repo) => !linkedRepoNames.includes(repo.full_name));
      setAvailableRepos(available);
      setActiveModal('addRepo');
    } catch (err) {
      console.error('Failed to load repositories:', err);
      showToast('Failed to load available repositories', 'error');
    } finally {
      setIsLoadingRepos(false);
    }
  };

  const closeAddRepoModal = () => {
    if (activeModal === 'addRepo') {
      setActiveModal(null);
    }
    setSelectedRepoToAdd(null);
  };

  const handleAddRepo = async () => {
    if (!selectedRepoToAdd || !id) return;

    try {
      await addRepoMutation.mutateAsync(selectedRepoToAdd);
      closeAddRepoModal();
      await refreshProjectDetail();
      showToast('Repository added successfully', 'success');
    } catch (err) {
      console.error('Failed to add repository:', err);
      showToast('Failed to add repository', 'error');
    }
  };

  const handleDeleteRepo = async () => {
    if (!repoToDelete || !id) return;

    try {
      await deleteRepoMutation.mutateAsync(repoToDelete.id);
      setRepoToDelete(null);
      await refreshProjectDetail();
    } catch (err) {
      console.error('Failed to delete repository:', err);
      throw err;
    }
  };

  const handleOpenSlackModal = () => {
    if (slackConfig) {
      setSlackWebhookUrl(slackConfig.webhookUrl);
      setOriginalSlackWebhookUrl(slackConfig.webhookUrl);
    } else {
      setSlackWebhookUrl('');
      setOriginalSlackWebhookUrl('');
    }
    setActiveModal('slack');
  };

  const closeSlackModal = () => {
    if (activeModal === 'slack') {
      setActiveModal(null);
    }
  };

  const handleSaveSlack = async () => {
    if (!id || !slackWebhookUrl.trim()) return;

    try {
      await saveSlackMutation.mutateAsync(slackWebhookUrl);
      closeSlackModal();
      await refreshProjectDetail();
      showToast('Slack configured successfully', 'success');
    } catch (err) {
      console.error('Failed to save Slack config:', err);
      showToast('Failed to save Slack configuration', 'error');
    }
  };

  const handleDeleteSlack = async () => {
    if (!id) return;

    try {
      await deleteSlackMutation.mutateAsync();
      setSlackToDelete(false);
      await refreshProjectDetail();
      showToast('Slack configuration removed', 'success');
    } catch (err) {
      console.error('Failed to delete Slack config:', err);
      showToast('Failed to remove Slack configuration', 'error');
      throw err;
    }
  };

  const handleOpenEditBranchModal = async (config: EditBranchConfig) => {
    setEditBranchConfig(config);
    setSelectedBranch(config.defaultBranch);
    setActiveModal('editBranch');
    setIsLoadingBranches(true);
    try {
      const fetchedBranches = await queryClient.fetchQuery({
        queryKey: ['github-branches', config.repoOwner, config.repoName],
        queryFn: () => integrationsApi.getBranches(config.repoOwner, config.repoName),
        staleTime: 1000 * 60 * 5,
      });
      setBranches(fetchedBranches);
    } catch (err) {
      console.error('Failed to load branches:', err);
      showToast('Failed to load branches', 'error');
      setEditBranchConfig(null);
      setActiveModal(null);
    } finally {
      setIsLoadingBranches(false);
    }
  };

  const closeEditBranchModal = () => {
    if (activeModal === 'editBranch') {
      setActiveModal(null);
    }
    setEditBranchConfig(null);
  };

  const handleSaveBranch = async () => {
    if (!editBranchConfig || !id) return;

    try {
      await saveBranchMutation.mutateAsync({
        configId: editBranchConfig.id,
        defaultBranch: selectedBranch,
      });
      closeEditBranchModal();
      await refreshProjectDetail();
      showToast('Default branch updated successfully', 'success');
    } catch (err) {
      console.error('Failed to update branch:', err);
      showToast('Failed to update default branch', 'error');
    }
  };

  const handleOpenJiraEditModal = async () => {
    if (!project?.jiraConfig?.jiraProjectKey) return;
    const jiraProjectKey = project.jiraConfig.jiraProjectKey;

    setActiveModal('jira');
    setIsLoadingJiraUsers(true);
    setIsLoadingJiraStatuses(true);
    setJiraConfigValidationError('');

    const currentAssignee = project.jiraConfig.assignee || '';
    const currentStartStatus = project.jiraConfig.startStatus || '';
    const currentEndStatus = project.jiraConfig.endStatus || '';
    setSelectedAssigneeAccountId(currentAssignee);
    setOriginalAssigneeAccountId(currentAssignee);
    setSelectedStartStatus(currentStartStatus);
    setOriginalStartStatus(currentStartStatus);
    setSelectedEndStatus(currentEndStatus);
    setOriginalEndStatus(currentEndStatus);

    try {
      const [users, statuses] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: ['jira-assignable-users', jiraProjectKey],
          queryFn: () => integrationsApi.getJiraAssignableUsers(jiraProjectKey),
          staleTime: 1000 * 60 * 5,
        }),
        queryClient.fetchQuery({
          queryKey: ['jira-project-statuses', jiraProjectKey],
          queryFn: () => integrationsApi.getJiraProjectStatuses(jiraProjectKey),
          staleTime: 1000 * 60 * 5,
        }),
      ]);
      setJiraAssignableUsers(users);
      setJiraStatuses(statuses);
    } catch (err) {
      console.error('Failed to fetch Jira data:', err);
      showToast('Failed to load Jira configuration data', 'error');
      setActiveModal(null);
    } finally {
      setIsLoadingJiraUsers(false);
      setIsLoadingJiraStatuses(false);
    }
  };

  const handleSaveJiraConfig = async () => {
    if (!project?.jiraConfig || !id) return;

    const hasStartStatus = selectedStartStatus.trim() !== '';
    const hasEndStatus = selectedEndStatus.trim() !== '';

    if (hasStartStatus !== hasEndStatus) {
      setJiraConfigValidationError('Both Start Status and End Status must be set together, or both left empty');
      return;
    }

    if (hasStartStatus && hasEndStatus && selectedStartStatus === selectedEndStatus) {
      setJiraConfigValidationError('Start Status and End Status must be different');
      return;
    }

    setJiraConfigValidationError('');

    try {
      const selectedUser = jiraAssignableUsers.find((userEntry) => userEntry.accountId === selectedAssigneeAccountId);

      await saveJiraConfigMutation.mutateAsync({
        jiraProjectKey: project.jiraConfig.jiraProjectKey,
        jiraProjectName: project.jiraConfig.jiraProjectName ?? undefined,
        jiraFilterJql: project.jiraConfig.jiraFilterJql ?? undefined,
        assignee: selectedAssigneeAccountId || undefined,
        assigneeDisplayName: selectedUser?.displayName || undefined,
        startStatus: selectedStartStatus || undefined,
        endStatus: selectedEndStatus || undefined,
      });

      if (activeModal === 'jira') {
        setActiveModal(null);
      }
      await refreshProjectDetail();
      showToast('Jira configuration updated successfully', 'success');
    } catch (err) {
      console.error('Failed to update Jira configuration:', err);
      showToast('Failed to update Jira configuration', 'error');
    }
  };

  const closeJiraEditModal = () => {
    if (activeModal === 'jira') {
      setActiveModal(null);
    }
  };

  const handleManualImport = async (ticketKey: string): Promise<boolean> => {
    try {
      await manualImportMutation.mutateAsync(ticketKey);
      showToast(`${ticketKey} queued for import`, 'success');
      return true;
    } catch (err) {
      console.error('Failed to import ticket:', err);
      const message = err instanceof Error ? err.message : 'Failed to import ticket';
      showToast(message, 'error');
      return false;
    }
  };

  const handleCancelTask = async (taskId: string) => {
    try {
      await cancelTaskMutation.mutateAsync(taskId);
      await queryClient.invalidateQueries({ queryKey: ['project-tickets', id] });
      showToast('Task cancelled', 'success');
    } catch (err) {
      console.error('Failed to cancel task:', err);
      const message = err instanceof Error ? err.message : 'Failed to cancel task';
      showToast(message, 'error');
    }
  };

  return {
    page: {
      id,
      isAdmin,
      activeTab,
      setActiveTab,
      project,
      slackConfig,
      ticketCount,
      isLoading,
      error,
      pinnedKnowledgeCount,
    },
    tickets: {
      items: tickets,
      hasMore: hasMoreTasks,
      isLoadingMore: isLoadingMoreTasks,
      isSearching,
      sentinelRef,
      searchQuery: taskSearchQuery,
      onSearchChange: handleSearchChange,
    },
    integrations: {
      githubStatus: githubIntegrationStatus,
      jiraStatus: jiraIntegrationStatus,
    },
    modal: {
      active: activeModal,
      addRepo: {
        isOpen: activeModal === 'addRepo',
        availableRepos,
        selectedRepoToAdd,
        setSelectedRepoToAdd,
        isLoadingRepos,
        open: handleOpenAddRepoModal,
        close: closeAddRepoModal,
      },
      editBranch: {
        isOpen: activeModal === 'editBranch' && !!editBranchConfig,
        config: editBranchConfig,
        branches,
        selectedBranch,
        setSelectedBranch,
        isLoadingBranches,
        open: handleOpenEditBranchModal,
        close: closeEditBranchModal,
      },
      slack: {
        isOpen: activeModal === 'slack',
        slackWebhookUrl,
        setSlackWebhookUrl,
        originalSlackWebhookUrl,
        open: handleOpenSlackModal,
        close: closeSlackModal,
      },
      jira: {
        isOpen: activeModal === 'jira',
        jiraAssignableUsers,
        jiraStatuses,
        selectedAssigneeAccountId,
        setSelectedAssigneeAccountId,
        originalAssigneeAccountId,
        selectedStartStatus,
        setSelectedStartStatus,
        selectedEndStatus,
        setSelectedEndStatus,
        originalStartStatus,
        originalEndStatus,
        isLoadingJiraUsers,
        isLoadingJiraStatuses,
        validationError: jiraConfigValidationError,
        open: handleOpenJiraEditModal,
        close: closeJiraEditModal,
      },
      confirmations: {
        repoToDelete,
        setRepoToDelete,
        slackToDelete,
        setSlackToDelete,
      },
    },
    actions: {
      addRepo: handleAddRepo,
      deleteRepo: handleDeleteRepo,
      saveBranch: handleSaveBranch,
      saveSlack: handleSaveSlack,
      deleteSlack: handleDeleteSlack,
      saveJiraConfig: handleSaveJiraConfig,
      manualImport: handleManualImport,
      cancelTask: handleCancelTask,
    },
    mutations: {
      isAddingRepo: addRepoMutation.isPending,
      isSavingBranch: saveBranchMutation.isPending,
      isSavingSlack: saveSlackMutation.isPending,
      isSavingJiraConfig: saveJiraConfigMutation.isPending,
      isImportingTicket: manualImportMutation.isPending,
    },
    toast: {
      state: toast,
      close: closeToast,
    },
  };
}
